import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import { GetUserSocietiesAPI, type UserSociety } from "@/services/api/society/getUserSocieties";
import { UploadEventAPI } from "@/services/api/organizer/uploadEvent";
import { UpdateEventAPI } from "@/services/api/organizer/editEvent";
import type { EventFormPayload, StudentEvent } from "@/services/api/user/events";
import { emptyEventForm, toEventFormPayload } from "@/utils/eventFormHydrate";
import EventFormSocietyStep from "./EventFormSocietyStep";
import EventFormInfoStep from "./EventFormInfoStep";
import EventFormDateStep from "./EventFormDateStep";

const STEP_COUNT = 3;

const STEP_KEYS = [
  "Your Societies / Clubs*",
  "Title*",
  "Date & Time*",
] as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[\d\s()+-]{5,}$/;

interface EventFormWizardProps {
  mode: "create" | "edit";
  /** Existing event, required in edit mode. */
  event?: StudentEvent | null;
}

/**
 * Three-step organizer event form, shared by the create and edit screens and
 * modelled on the RN wizard (`SocietyAndImageScreen` → `EventInformationScreen`
 * → `DateAndTimeScreen`).
 */
const EventFormWizard: React.FC<EventFormWizardProps> = ({ mode, event }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [payload, setPayload] = useState<EventFormPayload>(emptyEventForm());
  const [societies, setSocieties] = useState<UserSociety[]>([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    GetUserSocietiesAPI().then((result) => {
      if (cancelled) return;
      setLoadingSocieties(false);
      setSocieties(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode === "edit" && event) {
      setPayload(toEventFormPayload(event, societies));
    }
    // Hydrate once the societies are known, so the university codes line up.
  }, [mode, event, societies]);

  const update = useCallback(
    <K extends keyof EventFormPayload>(key: K, value: EventFormPayload[K]) => {
      setPayload((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const validateStep = (target: number): boolean => {
    if (target === 1) {
      if (societies.length === 0) {
        setAlertText(t("Join a society to create an event"));
        return false;
      }
      if (payload.societyIds.length === 0) {
        setAlertText(t("Please Select Event Society"));
        return false;
      }
    }

    if (target === 2) {
      if (!payload.title.trim()) {
        setAlertText(t("Please Input Event Title"));
        return false;
      }
      const invalidEmail = payload.emailList.find(
        (item) => item.input2.trim() && !EMAIL_PATTERN.test(item.input2.trim()),
      );
      if (invalidEmail) {
        setAlertText(t("Invalid Email"));
        return false;
      }
      const invalidPhone = payload.phoneList.find(
        (item) => item.input2.trim() && !PHONE_PATTERN.test(item.input2.trim()),
      );
      if (invalidPhone) {
        setAlertText(t("Invalid Phone Number"));
        return false;
      }
    }

    if (target === 3) {
      if (!payload.startDate) {
        setAlertText(t("Please Select Event Start Date"));
        return false;
      }
      if (!payload.endDate) {
        setAlertText(t("Please Select Event End Date"));
        return false;
      }
      if (payload.endDate < payload.startDate) {
        setAlertText(t("End date cannot be earlier than the start date"));
        return false;
      }
    }

    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, STEP_COUNT));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (step === 1) {
      navigate(-1);
      return;
    }
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) return;

    const cleanPayload: EventFormPayload = {
      ...payload,
      title: payload.title.trim(),
      phoneList: payload.phoneList.filter((item) => item.input2.trim()),
      emailList: payload.emailList.filter((item) => item.input2.trim()),
      linkList: payload.linkList.filter((item) => item.input2.trim()),
      tagList: payload.tagList.map((tag) => tag.trim()).filter(Boolean),
    };

    setSubmitting(true);
    const eventId = event?.details.id;
    const resp =
      mode === "create" || !eventId
        ? await UploadEventAPI(cleanPayload)
        : await UpdateEventAPI(eventId, cleanPayload);
    setSubmitting(false);

    if (resp?.status !== 0) {
      setAlertText(t(mode === "create" ? "Can`t Upload Event" : "Can`t Edit Event"));
      return;
    }

    setSuccessText(
      t(mode === "create" ? "Create Event Successfully!" : "Edit Event Successfully!"),
    );
    setPendingEventId(resp.result?.id ?? eventId ?? null);
  };

  const closeSuccess = () => {
    const target = pendingEventId ?? event?.details.id;
    setSuccessText("");
    setPendingEventId(null);
    if (target) navigate(`/student/events/${target}`, { replace: true });
  };

  const steps = STEP_KEYS.map((key, index) => {
    const number = index + 1;
    const state = number < step ? "done" : number === step ? "active" : "todo";
    return (
      <li key={key} className="flex flex-1 items-center gap-2">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold transition ${
            state === "todo"
              ? "bg-gray-100 text-gray-400"
              : "bg-[#ffa217] text-white"
          }`}
        >
          {state === "done" ? <Check size={14} /> : number}
        </span>
        <span
          className={`hidden truncate text-[12px] font-semibold sm:block ${
            state === "active" ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {t(key)}
        </span>
        {number < STEP_COUNT ? (
          <span
            className={`h-0.5 flex-1 rounded ${
              state === "done" ? "bg-[#ffa217]" : "bg-gray-100"
            }`}
          />
        ) : null}
      </li>
    );
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900">
        {t(mode === "create" ? "Create Event" : "Edit Event")}
      </h1>
      <p className="mt-1 text-xs text-gray-500">
        {t("Step {{current}} of {{total}}", { current: step, total: STEP_COUNT })}
      </p>

      <ol className="mt-4 flex items-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
        {steps}
      </ol>

      <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
        {loadingSocieties ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={22} className="animate-spin text-[#ffa217]" />
          </div>
        ) : step === 1 ? (
          <EventFormSocietyStep
            societies={societies}
            payload={payload}
            onChange={update}
          />
        ) : step === 2 ? (
          <EventFormInfoStep
            payload={payload}
            onChange={update}
            tagsReadOnly={mode === "edit"}
          />
        ) : (
          <EventFormDateStep payload={payload} onChange={update} />
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={goBack}
          className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          {t("Back")}
        </button>

        {step < STEP_COUNT ? (
          <button
            type="button"
            onClick={goNext}
            className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:opacity-90"
          >
            {t("Next")}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t("Uploading...")}
              </>
            ) : (
              t(mode === "create" ? "UPLOAD EVENT" : "EDIT EVENT")
            )}
          </button>
        )}
      </div>

      <Modal visible={Boolean(alertText)} onClose={() => setAlertText("")}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{alertText}</p>
          <button
            type="button"
            onClick={() => setAlertText("")}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      <Modal visible={Boolean(successText)} onClose={closeSuccess}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{successText}</p>
          <button
            type="button"
            onClick={closeSuccess}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EventFormWizard;
